import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faLandmarkDome } from '@fortawesome/free-solid-svg-icons';
import { useBranding } from 'modules/Common/hooks/useBranding';

// Which file (or files) an upload step accepts — the answer to "what am I
// supposed to upload?", which is the question that stalls these steps.
//
// Read-only by design (D5): every option goes to the same Upload hub, so the
// step keeps ONE Upload button underneath rather than repeating a control per
// row. The rows say what the choice is; the button acts on it.
//
// One block holding both, not a card each. A bordered white card per option is
// what made them look pressable — same shape and elevation as a button. A
// single tinted panel split by a hairline groups them as one piece of
// information instead, which is what they are. Stripping the container
// entirely was tried and read as worse: the rows lost the thing that says
// "these two belong together and are the same kind of thing".
// Written out rather than computed, so the call carries an English default.
// Namespaced explicitly: this is a module-scope helper, so the extractor has no
// enclosing useTranslation() to infer from and would otherwise file these under
// `common` while the runtime `t` resolves them against `onboarding` — the
// silent split documented in docs/features/declarant-i18n/CONVENTIONS.md.
// The sheet is named after whoever's product this is — "Carbon Glance
// Template" on a whitelabel build would name a company the user has never
// heard of. Same {{brand}} interpolation the import wizard uses
// (common:...useUploadTemplateInstead).
const optionTitle = (id, t, brandName) => {
  if (id === 'eu-comms')
    return t('onboarding:uploads.euComms.title', 'CBAM Communication Template');
  // Same name the /uk/imports source picker uses (uk.sourcePicker.hmrcTitle) —
  // one document, one name, wherever it appears.
  if (id === 'hmrc-export') return t('onboarding:uploads.hmrcExport.title', 'HMRC Customs Report');
  return t('onboarding:uploads.cgSheet.title', '{{brand}} Template', { brand: brandName });
};

const ICONS = {
  excel: faFileExcel,
  eu: faLandmarkDome,
  // Both are government files, so both wear the government glyph.
  hmrc: faLandmarkDome,
};

// Two option rows plus the action row is the panel's tallest case (the
// emissions step), so the padding here is what decides whether the rail scrolls.
const OnboardingUploadOptions = ({ options, hint }) => {
  const { t } = useTranslation('onboarding');
  const brand = useBranding();
  return (
    <div className="mb-1.5">
      {hint && <p className="text-[11px] text-slate-500 leading-snug m-0 mb-1">{hint}</p>}
      <div className="rounded-lg bg-slate-50/80 border border-slate-100 divide-y divide-slate-200/70">
        {options.map((option) => (
          // `items-start`: a blurb that wraps to two lines must not drag the icon
          // down to the middle of the row, away from the title it belongs to.
          <div
            key={option.id}
            className="px-2.5 py-1.5 flex items-start gap-2"
          >
            <FontAwesomeIcon
              icon={ICONS[option.icon]}
              className={`text-[13px] shrink-0 mt-[3px] ${
                option.icon === 'excel'
                  ? 'text-primaryColor ml-[1.625px] mr-[1.625px]'
                  : 'text-slate-400'
              }`}
            />
            <div className="flex-1 min-w-0 leading-tight">
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] font-semibold text-slate-700 truncate">
                  {optionTitle(option.id, t, brand.name)}
                </span>
                {option.badge && (
                  <span className="text-[8.5px] font-bold uppercase tracking-wider text-primaryColor-dark bg-primaryColor/10 px-1.5 py-px rounded shrink-0">
                    {option.badge}
                  </span>
                )}
              </div>
              {/* Wraps rather than truncates: this line is the whole reason the
                row exists — "which file do I bring?" — and an ellipsis cuts it
                off exactly where the answer is. Two lines is what the blurbs
                are written to fit, at both the Home and floating widths. */}
              <div className="text-[10.5px] text-slate-500 leading-snug mt-px">{option.blurb}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OnboardingUploadOptions;
